import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white text-slate-900">
      {/* Main */}
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-[420px]">
          {/* Icon + title */}
          <div className="mb-10 flex flex-col items-center text-center">
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-[18px] bg-[#0071e3] shadow-sm" data-testid="login-logo">
              <Wrench className="h-8 w-8 text-white" strokeWidth={2.2} />
            </div>
            <h1 className="text-[26px] font-normal tracking-tight text-slate-900">
              Accedi a Global Service Exchange
            </h1>
          </div>

          <form onSubmit={submit} className="space-y-5">
            {error && (
              <div data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Combined field group */}
            <div
              className={`overflow-hidden rounded-2xl border transition-all duration-200 ${
                focused ? "border-[#f5c518] bg-[#fffdf3] ring-4 ring-[#fdf0c2]" : "border-slate-200 bg-white"
              }`}
            >
              <div className="relative border-b border-slate-200 px-4 pb-2 pt-3">
                <label htmlFor="email" className="block text-[11px] font-normal text-slate-500">
                  E-mail o numero di telefono
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  required
                  autoComplete="username"
                  className="w-full border-0 bg-transparent p-0 text-[17px] text-slate-900 outline-none placeholder:text-slate-400"
                />
              </div>
              <div className="relative px-4 pb-2 pt-3">
                <label htmlFor="password" className="block text-[11px] font-normal text-slate-500">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  required
                  autoComplete="current-password"
                  className="w-full border-0 bg-transparent p-0 text-[17px] tracking-widest text-slate-900 outline-none"
                />
              </div>
            </div>

            {/* Remember */}
            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                data-testid="login-remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 accent-[#0071e3]"
              />
              <label htmlFor="remember" className="text-[14px] text-slate-700">Ricordami</label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="flex w-full items-center justify-center rounded-full bg-[#0071e3] px-6 py-3.5 text-[16px] font-medium text-white transition-colors duration-200 hover:bg-[#0077ed] disabled:opacity-60"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accedi
            </button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 px-8 py-4">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 text-[12px] text-slate-500 sm:flex-row">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <span>Copyright © 2026 Dexter Lab. Tutti i diritti riservati.</span>
            <span className="font-medium text-slate-600">Informativa sulla privacy</span>
            <span className="hidden sm:inline">|</span>
            <span className="font-medium text-slate-600">Termini di utilizzo</span>
          </div>
          <span>Italia</span>
        </div>
      </footer>
    </div>
  );
}
